import type {
  Edge as ReactFlowEdge,
  EdgeProps as ReactFlowEdgeProps,
  Node as ReactFlowNode,
  Viewport,
} from '@xyflow/react';
import { NodesJobData } from './services/type';

export type WorkflowDataUpdater = {
  nodes: Node[];
  edges: Edge[];
  viewport: Viewport;
};

export enum NodeEnum {
  Start = 'start',
  // NLP 模型抽取
  NlpExtract = 'nlp_model_extraction',
  // 表格抽取节点
  TableExtraction = 'table_extraction',
  // 语义规则抽取节点
  SemanticRulesExtraction = 'semantic_rules_extraction',
  // 手写体抽取
  HandWritingExtraction = 'hand_writing_extraction',
  // 印章抽取节点
  SealExtraction = 'seal_extraction',
  // 大模型抽取节点
  LargeModelExtraction = 'large_model_extraction',
  // 人工添加抽取点
  ArtificialAddingExtraction = 'artificial-adding-extraction',
}

export type CommonNodeData<T = object> = {
  _connectedSourceHandleIds?: string[];
  _connectedTargetHandleIds?: string[];
  _isCandidate?: boolean;
  _isBundled?: boolean;
  _isEntering?: boolean;
  _selected?: boolean;
  _form?: NodesJobData;
  title?: string;
  valid?: boolean;
  desc: string;
  type: NodeEnum;
  /** 1 文本抽取点 2 表格抽取点 */
  tags?: { tag_name: string; table_name?: string; tag_type: 1 | 2 }[];
  width?: number;
  height?: number;
  name?: string;
} & T;

export type CommonEdgeType = {
  _hovering?: boolean;
  _connectedNodeIsHovering?: boolean;
  _connectedNodeIsSelected?: boolean;
  _isBundled?: boolean;
  sourceType: NodeEnum;
  targetType: NodeEnum;
};

export type Node<T = object> = ReactFlowNode<CommonNodeData<T>>;

export type SelectedNode = Pick<Node, 'id' | 'data'>;
export type NodeProps<T = unknown> = { id: string; data: CommonNodeData<T> };

export type Edge = Omit<ReactFlowEdge<CommonEdgeType>, 'data'> & {
  data: CommonEdgeType;
};
export type EdgeProps = ReactFlowEdgeProps<Edge>;
export type Block = {
  classification?: string;
  type: NodeEnum;
  title: string;
  description?: string;
};

export type NodeDefault<T extends Node> = {
  name: string;
  defaultData: { type: T['data']['type'] } & Partial<Omit<T['data'], 'type'>>;
  getAvailablePrevNodes: () => NodeEnum[];
  getAvailableNextNodes: () => NodeEnum[];
  icon: string;
  color: string;
  author: string;
  about: string;
  description?: string;
  deletable?: boolean;

  checkValid: (
    payload: T,
    t: any,
    moreDataForCheckValid?: any,
  ) => { isValid: boolean; errorMessage?: string };
};

export type OnSelectBlock = (type: NodeEnum) => void;

export type OnNodeAdd = (
  newNodePayload: {
    nodeType: NodeEnum;
    sourceHandle?: string;
    targetHandle?: string;
  },
  oldNodesPayload: {
    prevNodeId?: string;
    prevNodeSourceHandle?: string;
    nextNodeId?: string;
    nextNodeTargetHandle?: string;
  },
) => void;

export type NodePanelProps<T> = {
  id: string;
  data: CommonNodeData<T>;
  formRef: React.RefObject<BaseFormExposeRef>;
  initialValue?: any;
  node_id: string;
  readonly?: boolean;
};

/**
 * @description 通用表单组件props
 */
export interface BaseFormProps<T extends object = object> {
  initialValue?: Partial<T>;
  node_id: string;
  readonly?: boolean;
}
export type BaseFormExposeRef<R = any, T = object> = {
  assembleForm: () => Promise<R | undefined>;
} & T;
